import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Clock, Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Value shape:
//   { label, rolling, spanSec, fromSec, toSec }
//   - rolling=true  -> live window recomputed each fetch as [now-spanSec, now]
//   - rolling=false -> fixed absolute window [fromSec, toSec]
// ---------------------------------------------------------------------------
const DAY = 86400;

export function makeRolling(spanSec, label) {
  return { label, rolling: true, spanSec, fromSec: null, toSec: null };
}

export function resolveWindow(tr) {
  const now = Math.floor(Date.now() / 1000);
  if (!tr) return [now - 3600, now];
  if (tr.rolling) return [now - tr.spanSec, now];
  return [tr.fromSec, tr.toSec];
}

// span (seconds) of the current range — used to pick axis formatting / refresh cadence
export function rangeSpanSec(tr) {
  if (!tr) return 3600;
  if (tr.rolling) return tr.spanSec;
  return Math.max(1, (tr.toSec || 0) - (tr.fromSec || 0));
}

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const sec = (d) => Math.floor(d.getTime() / 1000);

function startOfWeek(d) {
  const x = startOfDay(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setDate(x.getDate() - day);
  return x;
}
function startOfMonth(d) { const x = startOfDay(d); x.setDate(1); return x; }

function absolute(fromSec, toSec, label) {
  return { label, rolling: false, spanSec: null, fromSec, toSec };
}

// Quick-range definitions grouped into columns (Grafana style).
const ROLLING = [
  ['Last 5 minutes', 300], ['Last 15 minutes', 900], ['Last 30 minutes', 1800],
  ['Last 1 hour', 3600], ['Last 3 hours', 10800], ['Last 6 hours', 21600],
  ['Last 12 hours', 43200], ['Last 24 hours', 86400],
];
const ROLLING_LONG = [
  ['Last 2 days', 2 * DAY], ['Last 7 days', 7 * DAY],
  ['Last 30 days', 30 * DAY], ['Last 90 days', 90 * DAY],
];

function calendarQuicks() {
  const now = new Date();
  const sod = startOfDay(now);
  const sow = startOfWeek(now);
  const som = startOfMonth(now);
  const prevWeekStart = new Date(sow); prevWeekStart.setDate(prevWeekStart.getDate() - 7);
  const prevMonthStart = new Date(som); prevMonthStart.setMonth(prevMonthStart.getMonth() - 1);
  const yStart = new Date(sod); yStart.setDate(yStart.getDate() - 1);
  const dbyStart = new Date(sod); dbyStart.setDate(dbyStart.getDate() - 2);
  return [
    absolute(sec(sod), sec(sod) + DAY, 'Today'),
    absolute(sec(sod), Math.floor(now.getTime() / 1000), 'Today so far'),
    absolute(sec(yStart), sec(sod), 'Yesterday'),
    absolute(sec(dbyStart), sec(yStart), 'Day before yesterday'),
    absolute(sec(sow), sec(sow) + 7 * DAY, 'This week'),
    absolute(sec(prevWeekStart), sec(sow), 'Previous week'),
    absolute(sec(som), sec(new Date(som.getFullYear(), som.getMonth() + 1, 1)), 'This month'),
    absolute(sec(prevMonthStart), sec(som), 'Previous month'),
  ];
}

// <input type="datetime-local"> helpers (local time).
function toLocalInput(sc) {
  if (!sc) return '';
  const d = new Date(sc * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function fromLocalInput(str) {
  if (!str) return null;
  const t = new Date(str).getTime();
  return Number.isNaN(t) ? null : Math.floor(t / 1000);
}

export default function TimeRangePicker({ value, onChange, testId = 'time-range' }) {
  const [open, setOpen] = useState(false);
  const [win] = useMemo(() => [resolveWindow(value)], [value]);
  const [fromStr, setFromStr] = useState(toLocalInput(win[0]));
  const [toStr, setToStr] = useState(toLocalInput(win[1]));
  const cal = useMemo(() => calendarQuicks(), [open]);

  const apply = (v) => { onChange?.(v); setOpen(false); };

  const applyAbsolute = () => {
    const f = fromLocalInput(fromStr);
    const t = fromLocalInput(toStr);
    if (f == null || t == null || t <= f) return;
    apply(absolute(f, t, `${fmtShort(f)} → ${fmtShort(t)}`));
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) { const w = resolveWindow(value); setFromStr(toLocalInput(w[0])); setToStr(toLocalInput(w[1])); }
  };

  const QuickBtn = ({ item }) => (
    <button
      data-testid={`${testId}-quick-${item.label.replace(/\s+/g, '-').toLowerCase()}`}
      onClick={() => apply(item.rolling !== undefined ? item : item)}
      className={cn(
        'block w-full text-left px-2 py-1.5 rounded text-[13px] transition-colors',
        value?.label === item.label
          ? 'bg-primary text-primary-foreground font-medium'
          : 'text-sky-300/90 hover:bg-accent hover:text-foreground'
      )}
    >
      {item.label}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline" size="sm"
          data-testid={`${testId}-trigger`}
          className="gap-2 font-normal max-w-[280px]"
        >
          <Clock className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">{value?.label || 'Pilih rentang waktu'}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(92vw,860px)] p-0 bg-[#0b1220] border-slate-700 text-slate-200"
        data-testid={`${testId}-panel`}
      >
        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr]">
          {/* Absolute range */}
          <div className="p-4 border-b md:border-b-0 md:border-r border-slate-700/70">
            <div className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wide">Rentang absolut</div>
            <label className="block text-[12px] text-slate-400 mb-1">From</label>
            <div className="flex items-center gap-2 mb-3">
              <CalendarIcon className="w-4 h-4 text-slate-500 shrink-0" />
              <Input
                type="datetime-local" value={fromStr}
                data-testid={`${testId}-from`}
                onChange={(e) => setFromStr(e.target.value)}
                className="h-9 bg-slate-900/70 border-slate-700 text-slate-100 [color-scheme:dark]"
              />
            </div>
            <label className="block text-[12px] text-slate-400 mb-1">To</label>
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon className="w-4 h-4 text-slate-500 shrink-0" />
              <Input
                type="datetime-local" value={toStr}
                data-testid={`${testId}-to`}
                onChange={(e) => setToStr(e.target.value)}
                className="h-9 bg-slate-900/70 border-slate-700 text-slate-100 [color-scheme:dark]"
              />
            </div>
            <Button
              className="w-full" size="sm" onClick={applyAbsolute}
              data-testid={`${testId}-apply`}
            >
              Apply
            </Button>
          </div>

          {/* Quick ranges */}
          <div className="p-4">
            <div className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wide">Rentang cepat</div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-0.5 max-h-[340px] overflow-auto">
              <div>
                {ROLLING_LONG.map(([label, s]) => <QuickBtn key={label} item={makeRolling(s, label)} />)}
              </div>
              <div>
                {cal.map((c) => <QuickBtn key={c.label} item={c} />)}
              </div>
              <div>
                {ROLLING.map(([label, s]) => <QuickBtn key={label} item={makeRolling(s, label)} />)}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function fmtShort(sc) {
  const d = new Date(sc * 1000);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
